import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
import { fromZXingFormat, toZXingFormats, type Symbology } from "./formats";

export interface DecodeResult {
  data: string;
  symbology: string;
  /** Corner points in source-image coordinates. */
  points: { x: number; y: number }[];
}

let prepared = false;
let wasmUrlOverride: string | undefined;

/**
 * Point the engine at a self-hosted zxing_reader.wasm (e.g. a bundler asset
 * URL). Without an override, zxing-wasm falls back to its version-pinned CDN.
 */
export function setWasmUrl(url: string): void {
  wasmUrlOverride = url;
}

export function getWasmUrl(): string | undefined {
  return wasmUrlOverride;
}

export async function prepareDecoder(): Promise<void> {
  if (prepared) return;
  if (wasmUrlOverride) {
    const url = wasmUrlOverride;
    prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) =>
          path.endsWith(".wasm") ? url : prefix + path,
      },
    });
  }
  prepared = true;
}

export async function decode(
  image: ImageData,
  symbologies: Symbology[],
  opts: { thorough?: boolean } = {},
): Promise<DecodeResult[]> {
  await prepareDecoder();
  // Fast mode is ~8x cheaper (measured in tools/benchmark/latency-test.mjs);
  // the scan loop runs it every frame and reserves thorough mode for a cadence.
  const thorough = opts.thorough ?? true;
  const results = await readBarcodes(image, {
    formats: toZXingFormats(symbologies),
    tryHarder: thorough,
    tryRotate: thorough,
    tryInvert: thorough,
    maxNumberOfSymbols: 8,
  });
  return results
    .filter((r) => r.isValid)
    .map((r) => ({
      data: r.text,
      symbology: fromZXingFormat(r.format),
      points: [
        r.position.topLeft,
        r.position.topRight,
        r.position.bottomRight,
        r.position.bottomLeft,
      ].map((p) => ({ x: p.x, y: p.y })),
    }));
}
